import { useState } from "react"
import { Link } from "react-router"
import {
  CustomSelectField,
  FormError,
  TextAreaField,
  usePublicForm,
} from "@client/modules/public/components"
import { Modal, ModalActions, ModalButton } from "@client/modules/admin/components/modal"
import { formatCurrency } from "./format"
import { useSponsorPortal } from "./sponsor-session-context"
import { DonorEmptyState } from "./components/empty-state"
import { SponsorshipStatusCard } from "./components/sponsorship-status"

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex flex-col justify-between gap-2 border border-gold-500 rounded-[10px] bg-white px-5 py-4 sm:h-[140px] sm:px-6">
      <span className="text-ink/50 text-base font-semibold">{label}</span>
      <span className="text-ink font-display text-[32px] font-bold leading-normal opacity-85">{value}</span>
    </div>
  )
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className} aria-hidden>
      <path d="M7 17L17 7M17 7H7M17 7V17" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}


const SPONSORSHIP_TYPES = ["Child sponsorship", "Women's empowerment", "Ability Program"] as const
const INTERESTS = [
  "One child, one term",
  "One child, one year",
  "One child, multi-year",
  "Multiple children",
  "Corporate / organization sponsorship",
  "One-off donation",
] as const

function SponsorModal({
  open,
  onClose,
  sponsor,
}: {
  open: boolean
  onClose: () => void
  sponsor: { name: string; email: string; phone: string | null }
}) {
  const { status, submit, reset } = usePublicForm("/api/public/inquiries")
  const [confirmedReset, setConfirmedReset] = useState(0)

  const handleSuccess = () => {
    setConfirmedReset((n) => n + 1)
    reset()
    onClose()
  }

  if (status === "success") {
    return (
      <Modal isOpen={open} onClose={handleSuccess} title="Thank you!">
        <p className="text-sm text-ink/70">
          Your inquiry is in. Our team will reach out to walk you through matching, funding, and updates.
        </p>
        <ModalActions>
          <ModalButton variant="primary" onClick={handleSuccess}>Done</ModalButton>
        </ModalActions>
      </Modal>
    )
  }

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Sponsor another child"
      subtitle="Fill out the form below"
    >
      <form
        key={confirmedReset}
        onSubmit={(event) => {
          const form = new FormData(event.currentTarget)
          const sponsorshipType = String(form.get("sponsorshipType") ?? "")
          const interest = String(form.get("interest") ?? "")
          const notes = String(form.get("notes") ?? "")
          const message = [
            `Sponsorship type: ${sponsorshipType}`,
            `Interest: ${interest}`,
            notes ? `\n${notes}` : "",
          ].join("\n")
          submit(event, {
            name: sponsor.name,
            email: sponsor.email,
            phone: sponsor.phone || undefined,
            type: "sponsorship",
            message,
          })
        }}
        className="flex flex-col gap-4"
      >
        <CustomSelectField label="Sponsorship type" name="sponsorshipType" options={SPONSORSHIP_TYPES} disabled={status === "loading"} />
        <CustomSelectField label="Sponsorship interest" name="interest" options={INTERESTS} disabled={status === "loading"} />
        <TextAreaField label="Anything we should know?" name="notes" placeholder="Optional..." disabled={status === "loading"} />
        <ModalActions>
          <ModalButton variant="secondary" onClick={onClose}>Cancel</ModalButton>
          <ModalButton type="submit" variant="primary" disabled={status === "loading"}>
            {status === "loading" ? "Submitting..." : "Submit inquiry"}
          </ModalButton>
        </ModalActions>
        {status === "error" && <FormError />}
      </form>
    </Modal>
  )
}

export function DonorOverviewPage() {
  const [sponsorModalOpen, setSponsorModalOpen] = useState(false)
  const portal = useSponsorPortal()
  if (!portal) return null
  const { sponsor, stats, recentUpdates, children, sponsorshipRequest } = portal
  const nextContribution = stats.nextContribution
    ? new Date(stats.nextContribution).toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "Not scheduled"

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-3 sm:grid-cols-3">
        <StatCard label="CHILDREN SPONSORED" value={stats.childrenSponsored} />
        <StatCard label="TOTAL GIVEN" value={formatCurrency(stats.totalGiven)} />
        <StatCard label="NEXT CONTRIBUTION" value={nextContribution} />
      </div>

      {sponsorshipRequest && (
        <SponsorshipStatusCard
          request={sponsorshipRequest}
          matchState={portal.matchState}
          activeMatchCount={portal.activeMatchCount}
        />
      )}

      {stats.totalGiven > 0 && children.length > 0 ? (
        <div className="border border-[#e2ded3] rounded-[10px] p-5 text-white sm:p-8" style={{ background: "linear-gradient(168deg, rgb(9, 49, 27) 0%, rgba(9, 49, 27, 0.8) 100%)" }}>
          <h2 className="m-0 font-display text-2xl font-bold leading-normal tracking-tight text-[#faf6ee] sm:text-[30px]">
            Thank you, {sponsor.name.split(" ")[0]}.
          </h2>
          <p className="mt-2 text-base font-normal leading-normal text-[rgba(250,246,238,0.85)] max-w-xl">
            Your giving this year covered a full year of fees, books, and uniforms for {stats.childrenSponsored} {stats.childrenSponsored === 1 ? "scholar" : "scholars"}. Read their latest updates below.
          </p>
          <div className="flex flex-wrap gap-3 mt-5">
            <Link
              to="/donor/children"
              className="inline-flex w-full items-center justify-center gap-2 h-12 px-5 pr-2 bg-cream-50 text-[#09311b] text-base font-medium rounded-full no-underline hover:bg-cream-100 transition-colors sm:w-auto sm:justify-start"
            >
              See my children
              <span className="flex items-center justify-center w-7 h-7 bg-forest-950 rounded-full">
                <ArrowIcon className="w-4 h-4 text-white" />
              </span>
            </Link>
            <button
              type="button"
              onClick={() => setSponsorModalOpen(true)}
              className="inline-flex w-full items-center justify-center h-12 px-5 border border-cream-50 text-base font-medium text-white rounded-full bg-transparent hover:bg-white/10 transition-colors sm:w-auto"
            >
              Sponsor another child
            </button>
          </div>
        </div>
      ) : !sponsorshipRequest ? (
        <DonorEmptyState
          title={children.length === 0
            ? (portal.donations.length > 0 ? "Your contribution is being processed" : "Your sponsorship story starts here")
            : "Your sponsorship is ready"
          }
          description={children.length === 0
            ? (portal.donations.length > 0
              ? "Your contribution is in our records. Giving history and sponsorship matching will update here as processing is completed."
              : "Choose a sponsorship to begin. Your contribution, sponsored children, and updates will all be connected to this workspace."
            )
            : "Your sponsored children are ready to explore. Their profiles and updates will appear on the children page."
          }
          primaryLabel={children.length === 0
            ? (portal.donations.length > 0 ? "Contact Redempta" : "Start a sponsorship")
            : "See my children"
          }
          primaryTo={children.length === 0
            ? (portal.donations.length > 0 ? "/contact" : "/sponsor")
            : "/donor/children"
          }
        />
      ) : null}

      <div className="grid gap-3 md:grid-cols-2">
        {recentUpdates.length === 0 ? (
          (children.length > 0 || stats.totalGiven > 0) && (
            <DonorEmptyState
              title="Updates will appear here"
              description="Reports and progress updates from your sponsored children will be added here as they are uploaded."
            />
          )
        ) : recentUpdates.map((update) => {
          const child = children.find((item) => item.id === update.childId)
          return (
          <article key={update.id} className="flex gap-4 border border-[#e2ded3] rounded-[10px] bg-white p-5 min-h-[134px]">
            {child?.photoUrl && <img src={child.photoUrl} alt={child.name} className="w-20 h-20 flex-shrink-0 rounded-[20px] object-cover" />}
            <div className="flex-1 min-w-0 flex flex-col gap-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                <h3 className="m-0 text-[#0b140d] font-serif text-lg font-normal leading-7 tracking-tight">{update.childName}</h3>
                <time className="text-[#4e594f] text-xs font-normal whitespace-nowrap">{new Date(update.date).toLocaleDateString()}</time>
              </div>
              <p className="m-0 text-[#4e594f] text-sm font-normal leading-5 line-clamp-3">{update.description}</p>
            </div>
          </article>
          )
        })}
      </div>

      <SponsorModal sponsor={sponsor} open={sponsorModalOpen} onClose={() => setSponsorModalOpen(false)} />
    </div>
  )
}
